'use client';

import React, { useState, useEffect, useCallback, MouseEvent } from 'react';
import { ToggleSwitch } from './ToggleSwitch';
import { ConsentStatus, EConsentStatus, TranslateFn } from '../types';

interface CookieSettingsDialogProps {
    t: TranslateFn;
    onClose: () => void;
    onSave: (enableAnalytics: boolean) => void;
    consentStatus: ConsentStatus;
}

export function CookieSettingsDialog({ t, onClose, onSave, consentStatus }: CookieSettingsDialogProps) {
    const [enableAnalytics, setEnableAnalytics] = useState(
        consentStatus !== null ? consentStatus === EConsentStatus.GRANTED : true
    );

    useEffect(() => {
        if (consentStatus !== null) {
            setEnableAnalytics(consentStatus === EConsentStatus.GRANTED);
        }
    }, [consentStatus]);

    const handleSaveSettings = useCallback(() => {
        onSave(enableAnalytics);
        onClose();
    }, [enableAnalytics, onSave, onClose]);

    const handleToggleAnalytics = useCallback(() => {
        setEnableAnalytics(prev => !prev);
    }, []);

    const handleDialogClick = useCallback((event: MouseEvent) => {
        event.stopPropagation();
    }, []);

    return (
        <div
            className="relative rounded-[10px] max-w-[618px] w-11/12 lg:w-full p-6 lg:p-12 box-border bg-[rgba(0,0,0,0.6)] text-white z-[10000] max-h-[85dvh] overflow-y-auto"
            style={{
                willChange: 'transform',
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden' as const,
                WebkitBackdropFilter: 'blur(2px)',
                backdropFilter: 'blur(2px)',
            }}
            onClick={handleDialogClick}
            data-testid="cookie-settings-dialog"
        >
            <button
                type="button"
                className="absolute right-4 top-4 text-white text-xl leading-none"
                onClick={onClose}
                aria-label="Close cookie settings"
            >
                ×
            </button>
            <h3 className="text-lg font-semibold m-0 mb-6">
                {t('cookieConsent.cookiesSettings')}
            </h3>

            <div className="mb-6">
                <div className="flex justify-between items-center">
                    <p className="text-[14.4px] font-bold m-0">
                        {t('cookieConsent.strictlyNecessaryCookies.title')}
                    </p>
                    <ToggleSwitch checked={true} disabled={true} onChange={() => {}} />
                </div>
                <p className="text-sm font-light leading-relaxed mt-4 mb-8">
                    {t('cookieConsent.strictlyNecessaryCookies.description')}
                </p>
            </div>

            <div className="mb-6">
                <div className="flex justify-between items-center">
                    <p className="text-[14.4px] font-bold m-0">
                        {t('cookieConsent.analyticsCookies.title')}
                    </p>
                    <ToggleSwitch
                        checked={enableAnalytics}
                        onChange={handleToggleAnalytics}
                        dataTestId="analytics-toggle"
                    />
                </div>
                <p className="text-sm font-light leading-relaxed mt-4 mb-8">
                    {t('cookieConsent.analyticsCookies.description')}
                </p>
            </div>

           <div className='w-full flex justify-end'>
           <button
                type="button"
                onClick={handleSaveSettings}
                className="w-full cursor-pointer rounded-md border-none bg-[#A3E635] px-4 py-2 text-[#09090B] hover:opacity-90 lg:w-max"
                data-testid="cookie-save-settings-button"
            >
                {t('cookieConsent.saveSettings')}
            </button>
           </div>
        </div>
    );
}

export default CookieSettingsDialog;
